import { useEffect, useRef, type CSSProperties } from 'react';
import { cx } from '@parrot/utils';
import styles from './LoginBackdrop.module.css';

type Node = { tone: number; style: CSSProperties & Record<`--${string}`, string> };

const NODE_COUNT = 76;
const GOLDEN_ANGLE = 137.5;

/** Deterministic scatter, built once — same layout every render, no hydration mismatch. */
const NODES: Node[] = Array.from({ length: NODE_COUNT }, (_, i) => {
  const angle = (((i * GOLDEN_ANGLE) % 360) * Math.PI) / 180;
  const radius = 20 + ((i * 7.3) % 60);
  return {
    tone: i % 3,
    style: {
      '--node-x': `${Math.min(98, Math.max(2, 50 + radius * Math.cos(angle) * 0.8))}%`,
      '--node-y': `${Math.min(98, Math.max(2, 50 + radius * Math.sin(angle) * 0.8))}%`,
      '--node-delay': `${(i * 0.4) % 12}s`,
      '--node-duration': `${6 + ((i * 1.3) % 7)}s`,
    },
  };
});

const TONES = [styles.tonePrimary, styles.toneSecondary, styles.toneSuccess];

export const LoginBackdrop = () => {
  const baseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const base = baseRef.current;
    if (base === null) return;
    // Optional call: matchMedia is absent in jsdom and some embedded runtimes.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true) return;

    let frame = 0;
    const onMove = (event: MouseEvent) => {
      // Coalesce to one write per frame — mousemove fires far faster than paint.
      if (frame !== 0) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        base.style.setProperty('--pointer-x', `${event.clientX}px`);
        base.style.setProperty('--pointer-y', `${event.clientY}px`);
      });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div ref={baseRef} className={styles.base} aria-hidden={'true'}>
      <div className={styles.gradient} />
      <div className={styles.grid} />
      <div className={styles.nodes}>
        {NODES.map((node, i) => (
          <span key={i} className={cx(styles.node, TONES[node.tone])} style={node.style} />
        ))}
      </div>
      <span className={styles.pulse} />
      <span className={styles.pulse} />
      <div className={styles.tintTop} />
      <div className={styles.tintBottom} />
      <div className={styles.orb} />
    </div>
  );
};
